import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { PdfPayloadService } from 'src/app/services/pdf-payload-builder.service';
import { StorageService } from 'src/app/services/storage.service';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { LanguageService } from 'src/app/services/language.service';
@Component({
  selector: 'app-save-pdf-modal',
  templateUrl: './save-pdf-modal.component.html',
  styleUrls: ['./save-pdf-modal.component.scss'],
  standalone: false,
})
export class SavePdfModalComponent implements OnInit {
  generateUWReport: boolean = false;
  generateWerReport: boolean = false;
  generateSWSReport: boolean = false;
  generateComfortCalculator: boolean = false;
  // Show checkboxes based on validated data
  showUWReport: boolean = false;
  showWERReport: boolean = false;
  showSWSReport: boolean = false;
  showComfReport: boolean = false;
  isLoading: boolean = false;
  saveOnlyGlass: boolean = false;

  constructor(private storageService: StorageService, private modalController: ModalController,
    private pdfPayloadService: PdfPayloadService, private languageService: LanguageService,
  ) { }

  async ngOnInit() {
    try {
      const raw = await this.storageService.getProject();
      const project = raw ? JSON.parse(raw) : {};

      this.saveOnlyGlass = project?.saveOnlyGlass;
      if (!this.saveOnlyGlass)
        this.showUWReport = !!project?.uw;

      const wer = project?.windowEnergyRatingResult;
      this.showWERReport = !!(
        wer?.ukResult || wer?.chResult || wer?.dkResult ||
        wer?.frResult || wer?.pheResult
      );

      this.showSWSReport = !!project?.airSimulationResult;
      this.showComfReport = !!project?.comfortCalculator;

      // Default: pre-select all available
      this.generateUWReport = this.showUWReport;
      this.generateWerReport = this.showWERReport;
      this.generateSWSReport = this.showSWSReport;
      this.generateComfortCalculator = this.showComfReport;

    } catch (error) {
      console.error('Error loading project data:', error);
    }
  }

  get canDownload(): boolean {
    return !this.isLoading &&
      (this.generateUWReport || this.generateWerReport || this.generateSWSReport || this.generateComfortCalculator);
  }

  async downloadPDF() {
    if (!this.canDownload) return;
    this.isLoading = true;
    try {
      const language = this.languageService.getCurrentLanguage();
      const { bytes, fileName } = await this.pdfPayloadService.generatePdf({
        generateUWReport: this.generateUWReport,
        generateWerReport: this.generateWerReport,
        generateSWSReport: this.generateSWSReport,
        generateComfortCalculator: this.generateComfortCalculator,
        language
      });
      if (!Capacitor.isNativePlatform()) {
        // Web
        this.downloadOnWeb(bytes, fileName);
      } else {
        // Android / iOS
        await this.saveAndOpenOnDevice(bytes, fileName);
      }
      await this.modalController.dismiss(null, 'confirm');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private downloadOnWeb(base64: string, fileName: string) {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private async saveAndOpenOnDevice(base64: string, fileName: string) {
    // Save to Documents (or Data)
    const result = await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Documents,
    });

    // Try Share sheet — lets user choose viewer (Files, Drive, etc.)
    await Share.share({
      title: fileName,
      text: fileName,
      url: result.uri,
      dialogTitle: 'Open PDF',
    });
  }

  close() {
    this.modalController.dismiss();
  }
}
